import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useAtom } from "jotai";
import { useCallback, useState } from "react";
import { useErrorHandler } from "react-error-boundary";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { ActionButton } from "../../../fkc/components/shared/submit-button/submit-button.component";
import { updateFormioSubmission } from "../../../shared/api/yenta-api";
import { URL_REGEX } from "../../../shared/utils";
import {
  COMPANY_FORM_EXTRA_ENTRIES,
  COMPANY_FORM_TITLE,
  LOGO_URL,
  RE_WEBSITE_URL,
} from "../../restaurant-equippers.constants";
import {
  companyFormCompletedAtom,
  pagesAtom,
} from "../../restaurant-equippers.state";
import { CompanyFormData, Pages } from "../../restaurant-equippers.types";
import { FormHeader } from "../form-header/form-header.component";

export const companyFormSchema = z.object({
  companyUrl: z
    .string()
    .min(1, { message: "Company URL is required" })
    .regex(URL_REGEX, {
      message: "Please enter a valid URL",
    }),
  companyName: z.string().min(1, { message: "Company Name is required" }),
  contactName: z.string().min(1, { message: "Contact Name is required" }),
});

export function CompanyForm(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [, setPage] = useAtom(pagesAtom);
  const [, setCompanyFormCompleted] = useAtom(companyFormCompletedAtom);
  const handleWidgetError = useErrorHandler();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompanyFormData>({ resolver: zodResolver(companyFormSchema) });

  const goToConfirmation = useCallback((): void => {
    setPage(Pages.CONFIRMATION);
  }, [setPage]);

  const skip = useCallback((): void => {
    setPage(Pages.CONFIRMATION);
  }, [setPage]);

  const registerUser = useCallback(
    (companyForm: CompanyFormData): void => {
      setIsLoading(true);

      const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

      if (!submission) {
        throw new Error("No submission found");
      }

      const sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") || "";

      updateFormioSubmission(
        sweepFormUrl,
        {
          is_cpl_in_flow: true,
          cpl_in_flow_extra_entries: COMPANY_FORM_EXTRA_ENTRIES,
          ...submission,
        },
        handleWidgetError
      )
        .then(window.theme.Sweepstake.prototype.storeSubmission)
        .then(() =>
          window.theme.LyticsService.restaurantEquippersCompanyFormTracker(
            companyForm
          )
        )
        .then(() => setCompanyFormCompleted(true))
        .then(goToConfirmation)
        .catch(handleWidgetError)
        .finally(() => setIsLoading(false));
    },
    [handleWidgetError, goToConfirmation, setCompanyFormCompleted]
  );

  return (
    <>
      <FormHeader progress={100} />

      <div className="re__optin--content">
        <div className="re__optin--entries-badge">
          <span className="re__optin--entries-number">
            +{COMPANY_FORM_EXTRA_ENTRIES}
          </span>
          <span className="re__optin--entries-label">entries</span>
        </div>

        <div className="re__optin--question">{COMPANY_FORM_TITLE}</div>
      </div>

      <form className="re__optin--form" onSubmit={handleSubmit(registerUser)}>
        <div className="re__optin--input-container">
          <input
            className={`re__optin--input ${
              errors.companyUrl ? "re__optin--input--error" : ""
            }`}
            type="text"
            placeholder="Company URL"
            aria-label="Company URL"
            disabled={isLoading}
            {...register("companyUrl")}
          />

          <input
            className={`re__optin--input ${
              errors.companyName ? "re__optin--input--error" : ""
            }`}
            type="text"
            placeholder="Company Name"
            aria-label="Company Name"
            disabled={isLoading}
            {...register("companyName")}
          />

          <input
            className={`re__optin--input ${
              errors.contactName ? "re__optin--input--error" : ""
            }`}
            type="text"
            placeholder="Contact Name"
            aria-label="Contact Name"
            disabled={isLoading}
            {...register("contactName")}
          />

          {Object.keys(errors).length > 0 && (
            <ul className="re__optin--error">
              {Object.values(errors).map((error, index) => (
                <li key={`error-${index}`}>
                  <span aria-invalid="true">{error.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className={clsx(
            "lander__cpl__quiz__body",
            "re__content--buttons-body"
          )}
        >
          <div
            className={clsx(
              "lander__cpl__quiz__body--question-container",
              "no-margin"
            )}
          >
            <div className="lander__cpl__quiz__body--question-actions">
              <div
                className={clsx(
                  "lander__cpl__quiz__body--button-container",
                  "no-padding-top"
                )}
              >
                <ActionButton
                  type="submit"
                  className="full-width"
                  disabled={isLoading}
                >
                  Sign me up!
                </ActionButton>
                <button
                  className={clsx(
                    "lander-quiz--button",
                    "re__content--buttons-skip"
                  )}
                  onClick={skip}
                  type="button"
                >
                  No thanks
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="re__optin--logo-container">
          <a href={RE_WEBSITE_URL} target="_blank" rel="noopener noreferrer">
            <img
              src={LOGO_URL}
              alt="Restaurant Equippers Logo"
              className="re__optin--logo"
            />
          </a>
        </div>
      </form>
    </>
  );
}
